import { AwsRum, type AwsRumConfig } from 'aws-rum-web';

import { env } from '@/lib/env';
import type { Telemetry } from './index';
import { scrubData, scrubUrl } from './scrub';

/*
 * CloudWatch RUM adapter (spec §4.5). The only module that imports aws-rum-web.
 * Returns null when RUM env is unset so bootstrap can keep the no-op default.
 */
export function createRumTelemetry(): Telemetry | null {
  if (!env.RUM_APP_ID || !env.RUM_REGION || !env.RUM_IDENTITY_POOL) {
    return null;
  }

  let rum: AwsRum | undefined;

  const config: AwsRumConfig = {
    sessionSampleRate: env.RUM_SAMPLE_RATE,
    identityPoolId: env.RUM_IDENTITY_POOL,
    endpoint: `https://dataplane.rum.${env.RUM_REGION}.amazonaws.com`,
    telemetries: ['errors', 'performance'],
    allowCookies: true,
    enableXRay: true,
  };

  return {
    init() {
      rum = new AwsRum(
        env.RUM_APP_ID as string,
        '1.0.0',
        env.RUM_REGION as string,
        config,
      );
    },
    recordError(error, context) {
      rum?.recordError(
        error instanceof Error ? error : new Error(String(error)),
      );
      if (context) {
        rum?.recordEvent('error_context', scrubData(context) ?? {});
      }
    },
    recordEvent(name, data) {
      rum?.recordEvent(name, scrubData(data) ?? {});
    },
    recordPageView(routePattern) {
      // Record the matched pattern, never the concrete URL (no ids in RUM).
      rum?.recordPageView(scrubUrl(routePattern));
    },
    setUser(userId) {
      // A null id clears attribution (logout); an empty object removes the
      // previously set userId attribute for the remainder of the session.
      rum?.addSessionAttributes(userId === null ? {} : { userId });
    },
  };
}
